import type { CopilotStatefulToolCall } from '@customerio/hydra-copilot';
import { isEmptyString } from '@/utils/string';

export type ToolCallLabelSet = {
  planned: string;
  executing: string;
  success: string;
};

/**
 * Humanize a snake_case tool name: "search_docs" → "Search docs"
 */
function humanizeToolName(name: string): string {
  const label = name.replaceAll('_', ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * Derive human-readable labels for a tool call.
 * Uses backend summary when available, otherwise generates from tool name/args.
 */
export function getToolCallLabels(toolCall: CopilotStatefulToolCall): ToolCallLabelSet {
  const { name, summary } = toolCall;

  // Use backend summary when present (e.g. LLM-generated bash summaries)
  if (
    summary != null &&
    !isEmptyString(summary.planned) &&
    !isEmptyString(summary.executing) &&
    !isEmptyString(summary.success)
  ) {
    return {
      planned: summary.planned.trim().replace(/\.+$/, ''),
      executing: summary.executing.trim().replace(/\.+$/, '') + '…',
      success: summary.success.trim().replace(/\.+$/, ''),
    };
  }

  // Tool-specific fallbacks
  if (name === 'search_docs') {
    const query = typeof toolCall.args?.query === 'string' ? toolCall.args.query : '';
    const suffix = query.length > 0 ? ` for "${query}"` : '';
    return {
      planned: `Search docs${suffix}`,
      executing: `Searching docs${suffix}…`,
      success: `Searched docs${suffix}`,
    };
  }

  if (name === 'read') {
    return { planned: 'Read', executing: 'Reading', success: 'Read' };
  }

  if (name === 'write') {
    return { planned: 'Write', executing: 'Writing', success: 'Wrote' };
  }

  if (name === 'bash') {
    return { planned: 'Run script', executing: 'Running script…', success: 'Ran script' };
  }

  // Generic fallback
  const label = humanizeToolName(name);
  return { planned: label, executing: label + '…', success: label };
}

/**
 * Get the label for the tool call's current status.
 */
export function getToolCallLabel(toolCall: CopilotStatefulToolCall): string {
  const labels = getToolCallLabels(toolCall);
  if (toolCall.status === 'executing') return labels.executing;
  if (toolCall.status === 'success') return labels.success;
  return labels.planned;
}

/**
 * Short label for the chain-of-thought header (no query strings, etc.).
 */
export function getToolCallHeaderLabel(toolCall: CopilotStatefulToolCall): string {
  const { name, summary } = toolCall;

  // Backend summary — use it when all fields are present
  if (
    summary != null &&
    !isEmptyString(summary.planned) &&
    !isEmptyString(summary.executing) &&
    !isEmptyString(summary.success)
  ) {
    if (toolCall.status === 'executing') return summary.executing.trim().replace(/\.+$/, '') + '…';
    if (toolCall.status === 'success') return summary.success.trim().replace(/\.+$/, '');
    return summary.planned.trim().replace(/\.+$/, '');
  }

  if (name === 'search_docs') {
    if (toolCall.status === 'executing') return 'Searching docs…';
    if (toolCall.status === 'success') return 'Searched docs';
    return 'Search docs';
  }

  if (name === 'read') {
    const path = typeof toolCall.args?.path === 'string' ? toolCall.args.path : 'file';
    if (toolCall.status === 'executing') return `Reading ${path}`;
    if (toolCall.status === 'success') return `Read ${path}`;
    return `Read ${path}`;
  }

  if (name === 'write') {
    const path = typeof toolCall.args?.path === 'string' ? toolCall.args.path : 'file';
    if (toolCall.status === 'executing') return `Writing ${path}`;
    if (toolCall.status === 'success') return `Wrote ${path}`;
    return `Write ${path}`;
  }

  return getToolCallLabel(toolCall);
}
